import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Topbar from "../../components/common/Topbar";
import AppSidebar from "../../components/common/AppSidebar";
import Sidebar from "../../components/common/Sidebar";
import Chart from "../../components/trading/Chart";
import MarketTable from "../../components/trading/MarketTable";
import BottomPanel from "../../components/trading/BottomPanel";
import BuyModal from "../../components/modals/BuyModal";
import SellModal from "../../components/modals/SellModal";
import OrderModal from "../../components/modals/OrderModal";
import QuestionnaireModal from "../../components/modals/QuestionnaireModal";
import Toast from "../../components/common/Toast";
import Loader from "../../components/common/Loader";
import useWebSocket from "../../hooks/useWebSocket";
import OrderPanel from "../../components/trading/OrderPanel";
import Analytics from "../user/Analytics";
import {
  setCenterTab,
  setActiveInstrument,
  setActiveTimeframe,
  setBottomTab,
  openModal,
} from "../../store/uiSlice";
import {
  updateMarketData,
  setBalance,
  setPortfolio,
  setPendingOrders,
  setHistoryOrders,
} from "../../store/tradeSlice";
import { INSTRUMENTS, getLotSize } from "../../utils/constants";
import { fmtUSD, getPnlClass, fmtP as fmtPUtil } from "../../utils/formatters";
import { calcForexPnl } from "../../utils/forexUtils";
import api from "../../services/api";

const TIMEFRAME_LIST = ["5M", "15M", "1H", "1D", "1W"];
let _dataFetched = false;

const calcPnl = (pos, currentPrice) => {
  const cat = pos.category || "crypto";
  const instrumentId = pos.instrumentId;
  const ls = getLotSize(cat, instrumentId);
  const qty = Math.abs(Number(pos.qty) || 0);
  const avgPrice = Number(pos.avgPrice) || 0;
  const isShort = pos.direction === "sell" || Number(pos.qty) < 0;
  return calcForexPnl(
    isShort,
    currentPrice,
    avgPrice,
    qty,
    instrumentId,
    cat,
    ls,
  );
};

const calcPnlBP = (isShort, currentPrice, avgPrice, qty, instrumentId, cat) => {
  const ls = getLotSize(cat, instrumentId);
  return calcForexPnl(
    isShort,
    currentPrice,
    avgPrice,
    qty,
    instrumentId,
    cat,
    ls,
  );
};

const getTagColors = (tag) => {
  const colors = {
    bronze: "bg-amber-700/20 text-amber-400 border-amber-700/30",
    silver: "bg-gray-400/20 text-gray-300 border-gray-400/30",
    gold: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    diamond: "bg-cyan-400/20 text-cyan-300 border-cyan-400/30",
    platinum: "bg-purple-400/20 text-purple-300 border-purple-400/30",
    black: "bg-white/15 text-white border-white/25",
  };
  return colors[tag] || colors.bronze;
};

const fmtP = (price, cat) => {
  if (!price) return "—";
  if (cat === "forex") return price.toFixed(5);
  if (cat === "crypto")
    return (
      "$" +
      price.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  if (cat === "index") return price.toLocaleString();
  return (
    "$" +
    price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const fmtSL = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : "0.00");
const fmtTP = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : "0.00");

const fmtDT = (dt) => {
  if (!dt) return "—";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} | ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getDirectionLabel = (trade) => {
  const isLong = trade.direction === "buy";
  return { label: isLong ? "LONG" : "SHORT", color: isLong ? "long" : "short" };
};

const Badge = ({ children, color }) => {
  const map = {
    long: "bg-[rgba(16,185,129,0.18)] text-[#34d399] border-[rgba(16,185,129,0.3)]",
    short:
      "bg-[rgba(239,68,68,0.18)] text-[#f87171] border-[rgba(239,68,68,0.3)]",
    orange:
      "bg-[rgba(46,54,66,0.5)] text-[#94a3b8] border-[rgba(46,54,66,0.6)]",
    purple:
      "bg-[rgba(139,92,246,0.18)] text-[#a78bfa] border-[rgba(139,92,246,0.3)]",
    teal: "bg-[rgba(20,184,166,0.18)] text-[#2dd4bf] border-[rgba(20,184,166,0.3)]",
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold font-mono border whitespace-nowrap ${map[color]}`}
    >
      {children}
    </span>
  );
};

const EmptyState = ({ icon, text, sub, onAction, actionLabel }) => (
  <div className="flex flex-col items-center justify-center py-10 px-6 text-center h-full">
    <i
      className={`fas ${icon} text-4xl mb-4 opacity-20`}
      style={{ color: "var(--color-textSecondary)" }}
    />
    <p
      className="text-sm font-syne font-bold mb-2"
      style={{ color: "var(--color-text)" }}
    >
      {text}
    </p>
    {sub && (
      <p
        className="text-[11px] font-mono mb-6"
        style={{ color: "var(--color-textSecondary)" }}
      >
        {sub}
      </p>
    )}
    {onAction && actionLabel && (
      <button
        onClick={onAction}
        className="px-6 py-2.5 rounded-lg font-syne font-bold text-[12px] transition-all active:scale-95"
        style={{
          background: "var(--color-orangeBg)",
          border: "1px solid var(--color-orangeBorder)",
          color: "var(--color-text)",
        }}
      >
        <i className="fas fa-plus mr-1.5" /> {actionLabel}
      </button>
    )}
  </div>
);

// ═══════════════════════════════════════════════
// MOBILE ACCOUNT HUB
// ═══════════════════════════════════════════════
const MobileAccountHub = ({ balance, portfolio, marketData }) => {
  const { user } = useSelector((s) => s.auth);
  const { historyOrders } = useSelector((s) => s.trade);

  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [bonusBalance, setBonusBalance] = useState(0);

  useEffect(() => {
    api
      .get("/user/finance/summary")
      .then((r) => {
        setTotalDeposits(r.data.totalDeposits || 0);
        setTotalWithdrawals(r.data.totalWithdrawals || 0);
        setTotalProfit(r.data.totalProfit || 0);
        setBonusBalance(r.data.bonusBalance || 0);
      })
      .catch(() => {});
    api
      .get("/user/credit/requests")
      .then((r) => {
        if (r.data && Array.isArray(r.data)) {
          const approved = r.data
            .filter((r) => r.status === "approved")
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          setTotalCredit(approved);
        }
      })
      .catch(() => {});
  }, []);

  const calcOpenPnl = () =>
    Object.entries(portfolio).reduce((sum, [id, pos]) => {
      const cp =
        marketData[pos.instrumentId]?.price || Number(pos.avgPrice) || 0;
      return sum + calcPnl(pos, cp);
    }, 0);

  const calcMarginUsed = () =>
    Object.values(portfolio).reduce((s, p) => s + (Number(p.cost) || 0), 0);

  const creditLimit = Number(user?.creditLimit) || 0;
  const openPnl = calcOpenPnl();
  const equity = balance + openPnl;
  const marginUsed = calcMarginUsed();
  const creditUsed = Math.min(
    creditLimit,
    Math.max(0, marginUsed - balance - bonusBalance),
  );
  const creditAvailable = creditLimit - creditUsed;
  const freeMargin = equity - marginUsed;
  const marginLevel = marginUsed > 0 ? (equity / marginUsed) * 100 : 0;

  const winRate =
    historyOrders.length > 0
      ? (
          (historyOrders.filter((o) => o && (o.pnl || 0) > 0).length /
            historyOrders.filter((o) => o).length) *
          100
        ).toFixed(1)
      : 0;

  const tag = user?.tag || "bronze";
  const displayTag = tag.charAt(0).toUpperCase() + tag.slice(1);
  const leverage = user?.leverage || "1:100";

  const getMarginLevelColor = () => {
    if (marginUsed === 0) return "var(--color-textSecondary)";
    if (marginLevel > 200) return "var(--color-greenLight)";
    if (marginLevel > 100) return "var(--color-yellow)";
    return "var(--color-redLight)";
  };

  const getCreditUsedColor = () => {
    if (creditLimit === 0) return "var(--color-textSecondary)";
    const pct = (creditUsed / creditLimit) * 100;
    if (pct < 50) return "var(--color-greenLight)";
    if (pct < 80) return "var(--color-yellow)";
    return "var(--color-redLight)";
  };

  return (
    <div
      className="flex flex-col h-full overflow-y-auto custom-scrollbar"
      style={{ background: "var(--color-bg)" }}
    >
      <div
        className="relative mx-3 mt-3 mb-2 rounded-2xl overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, var(--color-orangeBg), var(--color-purpleBg))",
          border: "1px solid var(--color-orangeBorder)",
        }}
      >
        <div className="relative p-4 flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--color-gradientOrangePurple)" }}
          >
            <i
              className="fas fa-user text-base"
              style={{ color: "var(--color-textInverse)" }}
            />
          </div>
          <div className="min-w-0">
            <div
              className="font-syne text-base font-extrabold leading-tight truncate flex items-center gap-1.5"
              style={{ color: "var(--color-text)" }}
            >
              {user?.firstName
                ? `${user.firstName} ${user.lastName}`
                : "Trader"}
              <span
                className={`text-[7px] px-1.5 py-0.5 rounded font-mono font-bold border ${getTagColors(tag)}`}
              >
                {displayTag}
              </span>
            </div>
            <div
              className="text-[11px] font-mono font-bold truncate mt-0.5"
              style={{ color: "var(--color-textSecondary)" }}
            >
              {user?.email || "—"}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "var(--color-green)" }}
              />
              <span
                className="text-[10px] font-mono font-bold"
                style={{ color: "var(--color-green)" }}
              >
                LIVE ACCOUNT
              </span>
            </div>
          </div>
          <div className="ml-auto text-right flex-shrink-0">
            <div
              className="text-[9px] font-mono font-bold uppercase"
              style={{ color: "var(--color-textSecondary)" }}
            >
              Leverage
            </div>
            <div
              className="text-sm font-syne font-extrabold"
              style={{ color: "var(--color-text)" }}
            >
              {leverage}
            </div>
          </div>
        </div>
      </div>

      <div
        className="mx-3 mb-2 rounded-xl p-3"
        style={{
          background: "var(--color-bgSurface)",
          border: "1px solid var(--color-borderStrong)",
        }}
      >
        <div
          className="text-[9px] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5"
          style={{ color: "var(--color-purpleLight)" }}
        >
          <i className="fas fa-wallet text-[9px]" /> Account Status
        </div>
        {[
          {
            label: "Balance",
            icon: "fa-coins",
            iconColor: "var(--color-orange)",
            value: fmtUSD(balance),
            valueColor: "var(--color-text)",
          },
          {
            label: "Credit Limit",
            icon: "fa-credit-card",
            iconColor: "var(--color-blue)",
            value: fmtUSD(creditLimit),
            valueColor: "var(--color-blue)",
          },
          {
            label: "Credit Used",
            icon: "fa-credit-card-alt",
            iconColor: "var(--color-yellow)",
            value: fmtUSD(creditUsed),
            valueColor: getCreditUsedColor(),
          },
          {
            label: "Credit Avail",
            icon: "fa-unlock",
            iconColor: "var(--color-green)",
            value: fmtUSD(creditAvailable),
            valueColor:
              creditAvailable > 0
                ? "var(--color-green)"
                : "var(--color-redLight)",
          },
          {
            label: "Bonus",
            icon: "fa-gift",
            iconColor: "var(--color-purpleLight)",
            value: fmtUSD(bonusBalance),
            valueColor: "var(--color-purpleLight)",
          },
          {
            label: "Credit Given",
            icon: "fa-hand-holding-dollar",
            iconColor: "var(--color-orange)",
            value: fmtUSD(totalCredit),
            valueColor: "var(--color-orange)",
          },
          {
            label: "Open P&L",
            icon: "fa-chart-line",
            iconColor:
              openPnl >= 0 ? "var(--color-green)" : "var(--color-redLight)",
            value: (openPnl >= 0 ? "+" : "") + fmtUSD(openPnl),
            valueColor:
              openPnl >= 0 ? "var(--color-green)" : "var(--color-red)",
          },
          {
            label: "Equity",
            icon: "fa-scale-balanced",
            iconColor: "var(--color-purpleLight)",
            value: fmtUSD(equity),
            valueColor: "var(--color-text)",
          },
          {
            label: "Margin Used",
            icon: "fa-lock",
            iconColor: "var(--color-yellow)",
            value: fmtUSD(marginUsed),
            valueColor: "var(--color-yellow)",
          },
          {
            label: "Free Margin",
            icon: "fa-money-bill-wave",
            iconColor:
              freeMargin >= 0 ? "var(--color-green)" : "var(--color-redLight)",
            value: fmtUSD(freeMargin),
            valueColor:
              freeMargin >= 0 ? "var(--color-green)" : "var(--color-red)",
          },
          {
            label: "Margin Level",
            icon: "fa-shield-halved",
            iconColor: getMarginLevelColor(),
            value: marginUsed > 0 ? marginLevel.toFixed(1) + "%" : "—",
            valueColor: getMarginLevelColor(),
          },
        ].map((row, i, arr) => (
          <div
            key={row.label}
            className={`flex justify-between items-center py-2 ${i < arr.length - 1 ? "border-b" : ""}`}
            style={{ borderColor: "var(--color-borderLight)" }}
          >
            <span
              className="text-[10px] font-mono font-bold flex items-center gap-2"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className={`fas ${row.icon} text-[9px]`}
                style={{ color: row.iconColor }}
              />{" "}
              {row.label}
            </span>
            <span
              className="font-mono text-[11px] font-extrabold"
              style={{ color: row.valueColor }}
            >
              {row.value}
            </span>
          </div>
        ))}
        {marginUsed > 0 && marginLevel <= 120 && (
          <div
            className="mt-3 p-2.5 rounded-lg"
            style={{
              background: "var(--color-redBg)",
              border: "1px solid var(--color-redBorder)",
            }}
          >
            <p
              className="text-[9px] font-mono font-bold flex items-center gap-1.5"
              style={{ color: "var(--color-redLight)" }}
            >
              <i className="fas fa-exclamation-triangle text-[9px]" />
              {marginLevel <= 100
                ? "Margin Call - Add funds or close positions"
                : "Low Margin - Be careful"}
            </p>
          </div>
        )}
      </div>

      <div
        className="mx-3 mb-2 rounded-xl p-3"
        style={{
          background: "var(--color-bgSurface)",
          border: "1px solid var(--color-borderStrong)",
        }}
      >
        <div
          className="text-[9px] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5"
          style={{ color: "var(--color-purpleLight)" }}
        >
          <i className="fas fa-chart-simple text-[9px]" /> Trading Statistics
        </div>
        {[
          {
            label: "Total Deposits",
            icon: "fa-circle-plus",
            iconColor: "var(--color-green)",
            value: fmtUSD(totalDeposits),
            valueColor: "var(--color-green)",
          },
          {
            label: "Total Withdrawals",
            icon: "fa-circle-minus",
            iconColor: "var(--color-redLight)",
            value: fmtUSD(totalWithdrawals),
            valueColor: "var(--color-red)",
          },
          {
            label: "Total Profit",
            icon: "fa-trophy",
            iconColor: "var(--color-yellow)",
            value: (totalProfit >= 0 ? "+" : "") + fmtUSD(totalProfit),
            valueColor:
              totalProfit >= 0 ? "var(--color-green)" : "var(--color-red)",
          },
          {
            label: "Total Bonus",
            icon: "fa-gift",
            iconColor: "var(--color-purpleLight)",
            value: fmtUSD(bonusBalance),
            valueColor: "var(--color-purpleLight)",
          },
          {
            label: "Total Credit",
            icon: "fa-hand-holding-dollar",
            iconColor: "var(--color-orange)",
            value: fmtUSD(totalCredit),
            valueColor: "var(--color-orange)",
          },
          {
            label: "Total Trades",
            icon: "fa-fire",
            iconColor: "var(--color-orange)",
            value: historyOrders.length,
            valueColor: "var(--color-text)",
          },
          {
            label: "Win Rate",
            icon: "fa-bullseye",
            iconColor: "var(--color-purpleLight)",
            value: winRate + "%",
            valueColor: "var(--color-purpleLight)",
          },
        ].map((row, i, arr) => (
          <div
            key={row.label}
            className={`flex justify-between items-center py-2 ${i < arr.length - 1 ? "border-b" : ""}`}
            style={{ borderColor: "var(--color-borderLight)" }}
          >
            <span
              className="text-[10px] font-mono font-bold flex items-center gap-2"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className={`fas ${row.icon} text-[9px]`}
                style={{ color: row.iconColor }}
              />{" "}
              {row.label}
            </span>
            <span
              className="font-mono text-[11px] font-extrabold"
              style={{ color: row.valueColor }}
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div className="px-3 mb-3 grid grid-cols-2 gap-2">
        <button
          onClick={() => (window.location.href = "/deposit")}
          className="flex items-center justify-center gap-2 py-3.5 rounded-xl font-syne font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: "var(--color-gradientGreen)" }}
        >
          <i className="fas fa-circle-plus text-sm" /> Deposit
        </button>
        <button
          onClick={() => (window.location.href = "/withdrawal")}
          className="flex items-center justify-center gap-2 py-3.5 rounded-xl font-syne font-bold text-[13px] text-white transition-all active:scale-95"
          style={{ background: "var(--color-gradientRed)" }}
        >
          <i className="fas fa-circle-minus text-sm" /> Withdraw
        </button>
      </div>
      <div className="px-3 mb-4">
        <button
          onClick={() => (window.location.href = "/statement")}
          className="w-full py-3.5 rounded-xl font-syne font-bold text-[13px] transition-all active:scale-95"
          style={{
            background: "var(--color-purpleBg)",
            border: "1px solid var(--color-purpleBorder)",
            color: "var(--color-purpleLight)",
          }}
        >
          <i className="fas fa-file-invoice-dollar mr-1.5" /> Full Statement
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// MOBILE ORDERS PANEL
// ═══════════════════════════════════════════════
const MobileOrdersPanel = ({ portfolio, marketData }) => {
  const dispatch = useDispatch();
  const { bottomTab } = useSelector((s) => s.ui);
  const { pendingOrders, historyOrders, balance } = useSelector((s) => s.trade);
  const [isLoading, setIsLoading] = useState(false);

  const handleClosePosition = async (positionId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const position = portfolio[positionId];
      const currentPrice = marketData[position.instrumentId]?.price;
      if (!currentPrice) {
        alert("Price not available");
        return;
      }
      const response = await api.post("/trade/close", {
        instrumentId: position.instrumentId,
        positionId: positionId,
        closePrice: currentPrice,
      });
      if (response.data.success) {
        dispatch({
          type: "trade/setBalance",
          payload: response.data.newBalance,
        });
        dispatch({ type: "trade/removePosition", payload: positionId });
        window.dispatchEvent(new CustomEvent("trade-completed"));
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to close position");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.delete(`/orders/${orderId}`);
      if (response.data.success)
        dispatch({ type: "trade/removePendingOrder", payload: orderId });
    } catch (err) {
      alert(err.response?.data?.message || "Failed to cancel order");
    } finally {
      setIsLoading(false);
    }
  };

  const positions = Object.entries(portfolio);

  const totalLivePnl = Object.entries(portfolio).reduce((acc, [id, pos]) => {
    const rawQty = Number(pos.qty) || 0;
    const qty = Math.abs(rawQty);
    const avgPrice = Number(pos.avgPrice) || 0;
    const cat = pos.category || "crypto";
    const isShort = pos.direction === "sell" || rawQty < 0;
    const cp = Number(marketData[pos.instrumentId]?.price) || avgPrice;
    const instrumentId = pos.instrumentId;
    const ls = getLotSize(cat, instrumentId);
    return acc + calcPnlBP(isShort, cp, avgPrice, qty, instrumentId, cat);
  }, 0);

  const totalMarginUsed = Object.values(portfolio).reduce(
    (s, p) => s + (Number(p.cost) || 0),
    0,
  );
  const currentBalance = Number(balance) || 0;
  const equity = currentBalance + totalLivePnl;
  const freeMargin = equity - totalMarginUsed;
  const marginLevel =
    totalMarginUsed > 0 ? (equity / totalMarginUsed) * 100 : 0;

  const totalHistoryPnl = historyOrders.reduce(
    (acc, o) => acc + (Number(o.pnl) || 0) - (Number(o.swap) || 0),
    0,
  );

  const tabs = [
    {
      key: "active",
      label: "Active",
      icon: "fa-circle-check",
      count: positions.length,
    },
    {
      key: "pending",
      label: "Pending",
      icon: "fa-clock",
      count: pendingOrders.length,
    },
    {
      key: "history",
      label: "History",
      icon: "fa-clock-rotate-left",
      count: historyOrders.length,
    },
  ];

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ background: "var(--color-bg)" }}
    >
      <div
        className="flex-shrink-0 flex items-center px-2"
        style={{
          background: "var(--color-bgSurface)",
          borderBottom: "1px solid var(--color-borderStrong)",
          justifyContent: "space-around",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => dispatch(setBottomTab(tab.key))}
            className="flex items-center gap-1.5 px-3 py-3 text-[11px] font-syne font-bold transition-all"
            style={{
              color:
                bottomTab === tab.key
                  ? "var(--color-text)"
                  : "var(--color-textSecondary)",
              borderBottom:
                bottomTab === tab.key
                  ? "2px solid var(--color-orange)"
                  : "2px solid transparent",
            }}
          >
            <i className={`fas ${tab.icon} text-[11px]`} />
            {tab.label}
            {tab.count > 0 && (
              <span
                className="text-[8px] px-1.5 py-0.5 rounded-full"
                style={{
                  background:
                    bottomTab === tab.key
                      ? "var(--color-orangeBg)"
                      : "var(--color-bgCard)",
                  color:
                    bottomTab === tab.key
                      ? "var(--color-text)"
                      : "var(--color-textSecondary)",
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {bottomTab === "active" &&
          (positions.length === 0 ? (
            <EmptyState
              icon="fa-inbox"
              text="No active positions"
              sub="Tap a symbol in Market to start trading"
              onAction={() => dispatch(openModal("orderModal"))}
              actionLabel="New Order"
            />
          ) : (
            [...positions]
              .sort(([, a], [, b]) => {
                const dateA = new Date(a.openTime || a.createdAt || 0);
                const dateB = new Date(b.openTime || b.createdAt || 0);
                return dateB - dateA;
              })
              .map(([id, pos]) => {
                const rawQty = Number(pos.qty) || 0;
                const qty = Math.abs(rawQty);
                const avgPrice = Number(pos.avgPrice) || 0;
                const cat = pos.category || "crypto";
                const isShort = pos.direction === "sell" || rawQty < 0;
                const currentPrice =
                  marketData[pos.instrumentId]?.price || avgPrice;
                const instrumentId = pos.instrumentId;
                const ls = getLotSize(cat, instrumentId);
                const tradeVolume = (qty * ls).toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                });
                const pnl = calcPnlBP(
                  isShort,
                  currentPrice,
                  avgPrice,
                  qty,
                  instrumentId,
                  cat,
                );
                const swap = Number(pos.swap) || 0;
                const commission = Number(pos.commission) || 0;
                const margin = Number(pos.cost) || 0;
                const totalProfit = pnl - swap - commission;
                const posId = pos.id
                  ? String(pos.id).slice(-6)
                  : id.slice(0, 6);
                const sl = Number(pos.sl) || 0;
                const tp = Number(pos.tp) || 0;
                return (
                  <div
                    key={id}
                    className="mx-3 my-2 rounded-xl p-3"
                    style={{
                      background: "var(--color-bgSurface)",
                      border: "1px solid var(--color-borderStrong)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-1.5 h-1.5 rounded-full animate-pulse ${isShort ? "bg-[#ef4444]" : "bg-[#10b981]"}`}
                        />
                        <span
                          className="font-syne font-extrabold text-sm"
                          style={{ color: "var(--color-text)" }}
                        >
                          {pos.instrumentId}
                        </span>
                        <Badge color={isShort ? "short" : "long"}>
                          {isShort ? "SHORT" : "LONG"}
                        </Badge>
                      </div>
                      <span
                        className="text-[9px] font-mono font-bold"
                        style={{ color: "var(--color-textSecondary)" }}
                      >
                        #{posId}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Entry
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(avgPrice, cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Current
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(currentPrice, cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Total Profit
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              totalProfit >= 0
                                ? "var(--color-green)"
                                : "var(--color-red)",
                          }}
                        >
                          {totalProfit >= 0 ? "+$" : "-$"}
                          {Math.abs(totalProfit).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Amount
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {qty.toFixed(2)} lots
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Trade Vol
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {tradeVolume}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Open Time
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtDT(pos.openTime || pos.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          S/L
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              sl > 0
                                ? "var(--color-redLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtSL(pos.sl)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          T/P
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              tp > 0
                                ? "var(--color-greenLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtTP(pos.tp)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Margin
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {margin > 0 ? "$" + margin.toFixed(2) : "—"}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Swap
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              swap !== 0
                                ? "var(--color-orangeLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {swap !== 0
                            ? (swap < 0 ? "-$" : "$") +
                              Math.abs(swap).toFixed(2)
                            : "$0.00"}
                        </span>
                      </div>
                      {/* Commission cell removed — still subtracted in totalProfit above */}
                    </div>
                    <button
                      onClick={() => handleClosePosition(id)}
                      disabled={isLoading}
                      className="w-full py-2.5 rounded-lg font-syne font-bold text-[11px] transition-all active:scale-95 disabled:opacity-50"
                      style={{
                        background: "var(--color-redBg)",
                        border: "1px solid var(--color-redBorder)",
                        color: "var(--color-red)",
                      }}
                    >
                      <i className="fas fa-xmark mr-1.5" /> Close Position
                    </button>
                  </div>
                );
              })
          ))}

        {bottomTab === "pending" &&
          (pendingOrders.length === 0 ? (
            <EmptyState
              icon="fa-clock"
              text="No pending orders"
              sub="Place a Limit or Stop order"
              onAction={() => dispatch(openModal("orderModal"))}
              actionLabel="Create Order"
            />
          ) : (
            [...pendingOrders]
              .filter(Boolean)
              .sort((a, b) => {
                const dateA = new Date(a.createdAt || 0);
                const dateB = new Date(b.createdAt || 0);
                return dateB - dateA;
              })
              .map((order) => {
                const qty = Number(order.qty) || 0;
                const targetPrice = Number(order.targetPrice) || 0;
                const currentPrice = marketData[order.instId]?.price || 0;
                const dist = currentPrice
                  ? (
                      ((targetPrice - currentPrice) / currentPrice) *
                      100
                    ).toFixed(2)
                  : "—";
                const isBuy = order.direction === "buy";
                const instrumentId = order.instId;
                const cat = order.cat || "crypto";
                const tradeVolume = (
                  qty * getLotSize(cat, instrumentId)
                ).toLocaleString();
                const ordId = order.id ? String(order.id).slice(-6) : "—";
                const sl = Number(order.sl) || 0;
                const tp = Number(order.tp) || 0;
                return (
                  <div
                    key={order.id}
                    className="mx-3 my-2 rounded-xl p-3"
                    style={{
                      background: "var(--color-bgSurface)",
                      border: "1px solid var(--color-borderStrong)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-syne font-extrabold text-sm"
                          style={{ color: "var(--color-text)" }}
                        >
                          {order.instId}
                        </span>
                        <Badge color={isBuy ? "long" : "short"}>
                          {isBuy ? "LONG" : "SHORT"} {order.type?.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold"
                          style={{
                            background: "var(--color-orangeBg)",
                            color: "var(--color-text)",
                            border: "1px solid var(--color-orangeBorder)",
                          }}
                        >
                          PENDING
                        </span>
                        <span
                          className="text-[9px] font-mono font-bold"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          #{ordId}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Target
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(targetPrice, order.cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Current
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(currentPrice, order.cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Distance
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {dist}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Amount
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {qty.toFixed(2)} lots
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Trade Vol
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {tradeVolume}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          S/L
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              sl > 0
                                ? "var(--color-redLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtSL(order.sl)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          T/P
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              tp > 0
                                ? "var(--color-greenLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtTP(order.tp)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={isLoading}
                      className="w-full py-2.5 rounded-lg font-syne font-bold text-[11px] transition-all active:scale-95 disabled:opacity-50"
                      style={{
                        background: "var(--color-redBg)",
                        border: "1px solid var(--color-redBorder)",
                        color: "var(--color-red)",
                      }}
                    >
                      <i className="fas fa-times mr-1.5" /> Cancel Order
                    </button>
                  </div>
                );
              })
          ))}

        {bottomTab === "history" &&
          (historyOrders.length === 0 ? (
            <EmptyState
              icon="fa-clock-rotate-left"
              text="No trade history"
              sub="Your closed trades will appear here"
            />
          ) : (
            [...historyOrders]
              .filter(Boolean)
              .sort((a, b) => {
                const dateA = new Date(
                  a.closeTime || a.openTime || a.time || 0,
                );
                const dateB = new Date(
                  b.closeTime || b.openTime || b.time || 0,
                );
                return dateB - dateA;
              })
              .map((order, idx) => {
                const qty = Number(order.qty) || 0;
                const pnl = Number(order.pnl) || 0;
                const swap = Number(order.swap) || 0;
                const entryPrice = Number(order.entryPrice) || 0;
                const exitPrice = Number(order.exitPrice) || 0;
                // pnl already has commission deducted on the backend
                const netPnl = pnl - swap;
                const cat = order.cat || "crypto";
                const instrumentId = order.instId || order.instrumentId;
                const ls = getLotSize(cat, instrumentId);
                const tradeVolume = (qty * ls).toLocaleString();
                const ordId = order.id
                  ? String(order.id).slice(-6)
                  : String(idx + 1).padStart(6, "0");
                const dir = getDirectionLabel(order);
                const notional = entryPrice * qty * ls;
                const roi = notional > 0 ? (pnl / notional) * 100 : 0;
                const sl = Number(order.sl) || 0;
                const tp = Number(order.tp) || 0;
                return (
                  <div
                    key={order.id ?? idx}
                    className="mx-3 my-2 rounded-xl p-3"
                    style={{
                      background: "var(--color-bgSurface)",
                      border: "1px solid var(--color-borderStrong)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-syne font-extrabold text-sm"
                          style={{ color: "var(--color-text)" }}
                        >
                          {order.instId || order.instrumentId}
                        </span>
                        <Badge color={dir.color}>{dir.label}</Badge>
                        <Badge color="purple">FILLED</Badge>
                      </div>
                      <span
                        className="font-mono font-extrabold text-sm"
                        style={{
                          color:
                            netPnl >= 0
                              ? "var(--color-green)"
                              : "var(--color-red)",
                        }}
                      >
                        {netPnl >= 0 ? "+$" : "-$"}
                        {Math.abs(netPnl).toFixed(2)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Entry
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(entryPrice, cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Exit
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(exitPrice, cat)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Profit
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              netPnl >= 0
                                ? "var(--color-green)"
                                : "var(--color-red)",
                          }}
                        >
                          {netPnl >= 0 ? "+$" : "-$"}
                          {Math.abs(netPnl).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Amount
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {qty.toFixed(2)} lots
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Trade Vol
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {tradeVolume}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          ROI
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              roi >= 0
                                ? "var(--color-green)"
                                : "var(--color-red)",
                          }}
                        >
                          {roi >= 0 ? "+" : ""}
                          {roi.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          S/L
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              sl > 0
                                ? "var(--color-redLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtSL(order.sl)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          T/P
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{
                            color:
                              tp > 0
                                ? "var(--color-greenLight)"
                                : "var(--color-textDim)",
                          }}
                        >
                          {fmtTP(order.tp)}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Open Time
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtDT(order.openTime || order.time)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span
                          className="text-[8px] font-mono font-bold uppercase"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Close Time
                        </span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtDT(order.closeTime)}
                        </span>
                      </div>
                    </div>
                    <div
                      className="text-[9px] font-mono font-bold mt-2"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      #{ordId}
                    </div>
                  </div>
                );
              })
          ))}
      </div>

      <div
        className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 flex-wrap"
        style={{
          background: "var(--color-bgSurface)",
          borderTop: "1px solid var(--color-borderStrong)",
        }}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[8px] font-mono font-bold uppercase tracking-wide"
              style={{ color: "var(--color-textDim)" }}
            >
              {bottomTab === "history" ? "Closed Trades" : "Active Positions"}
            </span>
            <span
              className="text-[10.5px] font-mono font-extrabold"
              style={{ color: "var(--color-text)" }}
            >
              {bottomTab === "history"
                ? historyOrders.length
                : Object.keys(portfolio).length}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[8px] font-mono font-bold uppercase tracking-wide"
              style={{ color: "var(--color-textDim)" }}
            >
              Equity
            </span>
            <span
              className="text-[10.5px] font-mono font-extrabold"
              style={{ color: "var(--color-text)" }}
            >
              {fmtUSD(
                bottomTab === "history"
                  ? currentBalance + totalHistoryPnl
                  : equity,
              )}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[8px] font-mono font-bold uppercase tracking-wide"
              style={{ color: "var(--color-textDim)" }}
            >
              Free Margin
            </span>
            <span
              className="text-[10.5px] font-mono font-extrabold"
              style={{
                color:
                  (bottomTab === "history"
                    ? currentBalance + totalHistoryPnl
                    : freeMargin) >= 0
                    ? "var(--color-greenLight)"
                    : "var(--color-red)",
              }}
            >
              {fmtUSD(
                bottomTab === "history"
                  ? currentBalance + totalHistoryPnl
                  : freeMargin,
              )}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[8px] font-mono font-bold uppercase tracking-wide"
              style={{ color: "var(--color-textDim)" }}
            >
              Margin Level
            </span>
            <span
              className="text-[10.5px] font-mono font-extrabold"
              style={{
                color:
                  bottomTab === "history" || totalMarginUsed === 0
                    ? "var(--color-text)"
                    : marginLevel > 200
                      ? "var(--color-greenLight)"
                      : marginLevel > 100
                        ? "var(--color-yellow)"
                        : "var(--color-red)",
              }}
            >
              {bottomTab === "history" || totalMarginUsed === 0
                ? "—"
                : marginLevel.toFixed(1) + "%"}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-0.5 text-right">
          <span
            className="text-[8px] font-mono font-bold uppercase tracking-wide"
            style={{ color: "var(--color-textDim)" }}
          >
            {bottomTab === "history" ? "Profit/Losses" : "Floating P&L"}
          </span>
          <span
            className="font-mono font-extrabold text-base"
            style={{
              color:
                (bottomTab === "history" ? totalHistoryPnl : totalLivePnl) >= 0
                  ? "var(--color-green)"
                  : "var(--color-red)",
            }}
          >
            {(bottomTab === "history" ? totalHistoryPnl : totalLivePnl) >= 0
              ? "+"
              : ""}
            {fmtUSD(bottomTab === "history" ? totalHistoryPnl : totalLivePnl)}
          </span>
        </div>
      </div>
    </div>
  );
};

const MOBILE_MARKET_CATEGORIES = [
  { key: "all", label: "All", icon: "fa-globe" },
  { key: "forex", label: "Forex", icon: "fa-dollar-sign" },
  { key: "crypto", label: "Crypto", icon: "fa-bitcoin-sign" },
  { key: "metal", label: "Metals", icon: "fa-coins" },
  { key: "index", label: "Indices", icon: "fa-chart-line" },
  { key: "stock", label: "Stocks", icon: "fa-building" },
  { key: "commodity", label: "Commod", icon: "fa-oil-can" },
];

const MobileMarketCategoryBar = ({ activeCategory, onSelectCategory }) => {
  return (
    <div
      className="flex-shrink-0 overflow-x-auto custom-scrollbar"
      style={{
        background: "var(--color-bgSurface)",
        borderBottom: "1px solid var(--color-borderStrong)",
      }}
    >
      <div className="flex gap-1 px-2 py-2">
        {MOBILE_MARKET_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => onSelectCategory(cat.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold whitespace-nowrap transition-all border ${activeCategory === cat.key ? "" : "border-transparent"}`}
            style={{
              background:
                activeCategory === cat.key
                  ? "var(--color-orangeBg)"
                  : "transparent",
              borderColor:
                activeCategory === cat.key
                  ? "var(--color-orangeBorder)"
                  : "transparent",
              color:
                activeCategory === cat.key
                  ? "var(--color-text)"
                  : "var(--color-textSecondary)",
            }}
          >
            <i className={`fas ${cat.icon} text-[9px]`} />
            {cat.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const TradingTerminal = () => {
  const dispatch = useDispatch();
  const { centerTab, activeInstrument, activeTimeframe, bottomTab } =
    useSelector((s) => s.ui);
  const { balance, portfolio, marketData, pendingOrders, historyOrders } =
    useSelector((s) => s.trade);
  const { user } = useSelector((s) => s.auth);
  useWebSocket();
  const [isLoading, setIsLoading] = useState(!_dataFetched);
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [mobileTab, setMobileTab] = useState("table");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileOrderPopup, setShowMobileOrderPopup] = useState(false);
  const [mobileMarketCategory, setMobileMarketCategory] = useState("all");
  const [marketView, setMarketView] = useState("assets");
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem("marketFavorites");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [priceFlash, setPriceFlash] = useState({});
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [chartExpanded, setChartExpanded] = useState(false);
  const tickTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const storeRef = useRef({});
  const mountedRef = useRef(false);
  const currentTF = activeTimeframe || "1D";

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      dispatch(setActiveInstrument(e.detail));
      if (isMobile) {
        setMobileTab("chart");
        setShowMobileOrderPopup(true);
      }
    };
    window.addEventListener("instrument-selected", h);
    return () => window.removeEventListener("instrument-selected", h);
  }, [dispatch, isMobile]);

  useEffect(() => {
    const h = () => setShowQuestionnaire(true);
    window.addEventListener("open-questionnaire", h);
    return () => window.removeEventListener("open-questionnaire", h);
  }, []);

  useEffect(() => {
    const h = (e) => setChartExpanded(e.detail);
    window.addEventListener("toggle-chart-expand", h);
    return () => window.removeEventListener("toggle-chart-expand", h);
  }, []);

  const refreshUserData = useCallback(async () => {
    try {
      const [b, p, h] = await Promise.allSettled([
        api.get("/user/balance"),
        api.get("/user/portfolio"),
        api.get("/user/orders/history"),
      ]);
      if (b.status === "fulfilled")
        dispatch(setBalance(parseFloat(b.value.data.balance) || 0));
      if (p.status === "fulfilled") dispatch(setPortfolio(p.value.data));
      if (h.status === "fulfilled") {
        const historyData = Array.isArray(h.value.data) ? h.value.data : [];
        const uniqueHistory = historyData.filter(
          (trade, index, self) =>
            index === self.findIndex((t) => t.id === trade.id),
        );
        dispatch(setHistoryOrders(uniqueHistory));
      }
    } catch (e) {}
  }, [dispatch]);

  const fetchMarketData = useCallback(async () => {
    try {
      const r = await api.get(
        `/market/quotes?symbols=${INSTRUMENTS.map((i) => i.id).join(",")}`,
      );
      const m = {};
      const d = r.data;
      if (Array.isArray(d))
        d.forEach((q) => {
          if (q?.symbol) m[q.symbol] = q;
        });
      else if (d)
        Object.entries(d).forEach(([s, q]) => {
          if (q?.price) m[s] = { ...q, symbol: s };
        });
      const flashes = {};
      Object.keys(m).forEach((sym) => {
        const oldPrice = storeRef.current[sym]?.price;
        const newPrice = m[sym]?.price;
        if (oldPrice && newPrice && oldPrice !== newPrice)
          flashes[sym] = newPrice > oldPrice ? "up" : "down";
      });
      setPriceFlash(flashes);
      setTimeout(() => setPriceFlash({}), 600);
      storeRef.current = m;
      dispatch(updateMarketData(m));
    } catch (e) {}
  }, [dispatch]);

  const startTickSimulation = useCallback(() => {
    fetchMarketData();
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    tickTimerRef.current = setInterval(() => {
      fetchMarketData();
    }, 5000);
  }, [dispatch, fetchMarketData]);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      if (_dataFetched) {
        await fetchMarketData();
        startTickSimulation();
        refreshTimerRef.current = setInterval(fetchMarketData, 30000);
        if (mountedRef.current) {
          setConnectionStatus("connected");
          setIsLoading(false);
        }
        return;
      }
      setIsLoading(true);
      await Promise.allSettled([
        api
          .get("/user/balance")
          .then((r) => dispatch(setBalance(parseFloat(r.data.balance) || 0)))
          .catch(() => {}),
        api
          .get("/user/portfolio")
          .then((r) => dispatch(setPortfolio(r.data)))
          .catch(() => {}),
        api
          .get("/user/orders/pending")
          .then((r) =>
            dispatch(setPendingOrders(Array.isArray(r.data) ? r.data : [])),
          )
          .catch(() => {}),
        api
          .get("/user/orders/history")
          .then((r) => {
            const historyData = Array.isArray(r.data) ? r.data : [];
            const uniqueHistory = historyData.filter(
              (trade, index, self) =>
                index === self.findIndex((t) => t.id === trade.id),
            );
            dispatch(setHistoryOrders(uniqueHistory));
          })
          .catch(() => {}),
        api
          .get("/user/profile")
          .then((r) => {
            if (r.data?.leverage)
              dispatch({
                type: "auth/updateUser",
                payload: { leverage: r.data.leverage },
              });
          })
          .catch(() => {}),
      ]);
      await fetchMarketData();
      startTickSimulation();
      refreshTimerRef.current = setInterval(fetchMarketData, 30000);
      _dataFetched = true;
      if (mountedRef.current) {
        setConnectionStatus("connected");
        setIsLoading(false);
      }
    })().catch(() => {
      startTickSimulation();
      if (mountedRef.current) {
        setConnectionStatus("error");
        setIsLoading(false);
      }
    });
    return () => {
      mountedRef.current = false;
      clearInterval(tickTimerRef.current);
      clearInterval(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("trade-completed", refreshUserData);
    return () => window.removeEventListener("trade-completed", refreshUserData);
  }, [refreshUserData]);

  if (isLoading) return <Loader message="Loading terminal…" />;

  const mobileTabs = [
    { id: "table", icon: "fa-table", label: "Market" },
    { id: "chart", icon: "fa-chart-line", label: "Chart" },
    { id: "orders", icon: "fa-list-check", label: "Orders" },
    { id: "analytics", icon: "fa-chart-pie", label: "Analytics" },
    { id: "account", icon: "fa-user", label: "Account" },
  ];

  const getPriceFlashClass = (instrumentId) => {
    const flash = priceFlash[instrumentId];
    if (flash === "up") return "animate-flash-green";
    if (flash === "down") return "animate-flash-red";
    return "";
  };

  return (
    <div
      className="min-h-screen overflow-hidden"
      style={{ background: "var(--color-bg)" }}
    >
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{
            background:
              "radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)",
          }}
        />
      </div>

      {!isMobile ? (
        <div className="flex h-screen overflow-hidden">
          <AppSidebar />
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <div className="flex-shrink-0">
              <Topbar />
            </div>
            <div className="flex-1 flex overflow-hidden">
              {!chartExpanded && <Sidebar />}
              <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                <div
                  className="flex items-center gap-2 px-3 py-1.5 flex-shrink-0 overflow-x-auto"
                  style={{
                    background: "var(--color-bgSurface)",
                    borderBottom: "1px solid var(--color-borderMedium)",
                  }}
                >
                  <span
                    className="font-syne text-base font-extrabold whitespace-nowrap"
                    style={{ color: "var(--color-text)" }}
                  >
                    {activeInstrument?.id || "—"}
                  </span>
                  {activeInstrument && marketData[activeInstrument.id] && (
                    <div
                      className="flex items-center gap-3 ml-2 text-[9px] font-mono"
                      style={{ color: "var(--color-textDim)" }}
                    >
                      <span>
                        O
                        <b
                          className="ml-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(
                            marketData[activeInstrument.id].open,
                            activeInstrument.cat,
                          )}
                        </b>
                      </span>
                      <span>
                        H
                        <b
                          className="ml-1"
                          style={{ color: "var(--color-green)" }}
                        >
                          {fmtP(
                            marketData[activeInstrument.id].high,
                            activeInstrument.cat,
                          )}
                        </b>
                      </span>
                      <span>
                        L
                        <b
                          className="ml-1"
                          style={{ color: "var(--color-red)" }}
                        >
                          {fmtP(
                            marketData[activeInstrument.id].low,
                            activeInstrument.cat,
                          )}
                        </b>
                      </span>
                      <span>
                        C
                        <b
                          className={`ml-1 rounded px-1 ${getPriceFlashClass(activeInstrument.id)}`}
                          style={{ color: "var(--color-text)" }}
                        >
                          {fmtP(
                            marketData[activeInstrument.id].price,
                            activeInstrument.cat,
                          )}
                        </b>
                      </span>
                    </div>
                  )}
                  <div className="flex gap-0.5 ml-auto">
                    {TIMEFRAME_LIST.map((tf) => (
                      <button
                        key={tf}
                        onClick={() => dispatch(setActiveTimeframe(tf))}
                        className="px-2 py-1 rounded text-[9px] font-syne font-bold transition-all border"
                        style={{
                          background:
                            currentTF === tf
                              ? "var(--color-orangeBg)"
                              : "transparent",
                          borderColor:
                            currentTF === tf
                              ? "var(--color-orangeBorder)"
                              : "transparent",
                          color:
                            currentTF === tf
                              ? "var(--color-text)"
                              : "var(--color-textDim)",
                        }}
                      >
                        {tf}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  className="flex items-center flex-shrink-0 px-3"
                  style={{
                    background: "var(--color-bgSurface)",
                    borderBottom: "1px solid var(--color-borderMedium)",
                  }}
                >
                  {["chart", "table"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => dispatch(setCenterTab(tab))}
                      className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-syne font-bold cursor-pointer border-b-2 transition-all"
                      style={{
                        color:
                          centerTab === tab
                            ? "var(--color-text)"
                            : "var(--color-textMuted)",
                        borderColor:
                          centerTab === tab
                            ? "var(--color-orange)"
                            : "transparent",
                      }}
                    >
                      <i
                        className={`fas ${tab === "chart" ? "fa-chart-candlestick" : "fa-table"} text-[10px]`}
                      />
                      {tab === "chart" ? "Chart" : "Market Table"}
                    </button>
                  ))}
                  {centerTab === "table" && (
                    <div className="flex items-center ml-4">
                      <button
                        onClick={() => setMarketView("assets")}
                        className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-syne font-bold cursor-pointer border-b-2 transition-all"
                        style={{
                          color:
                            marketView === "assets"
                              ? "var(--color-text)"
                              : "var(--color-textMuted)",
                          borderColor:
                            marketView === "assets"
                              ? "var(--color-green)"
                              : "transparent",
                        }}
                      >
                        <i className="fas fa-boxes-stacked text-[10px]" />
                        Assets
                      </button>
                      <button
                        onClick={() => setMarketView("favorites")}
                        className="flex items-center gap-1.5 px-3 py-2 text-[10px] font-syne font-bold cursor-pointer border-b-2 transition-all"
                        style={{
                          color:
                            marketView === "favorites"
                              ? "var(--color-text)"
                              : "var(--color-textMuted)",
                          borderColor:
                            marketView === "favorites"
                              ? "var(--color-orange)"
                              : "transparent",
                        }}
                      >
                        <i className="fas fa-star text-[10px]" />
                        Favorites{" "}
                        {favorites.length > 0 && `(${favorites.length})`}
                      </button>
                    </div>
                  )}
                  <div className="ml-auto flex items-center pr-1 gap-1.5">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background:
                          connectionStatus === "connected"
                            ? "var(--color-green)"
                            : "var(--color-red)",
                      }}
                    />
                    <span
                      className="text-[9px] font-mono"
                      style={{ color: "var(--color-textDim)" }}
                    >
                      TF:{" "}
                      <span style={{ color: "var(--color-text)" }}>
                        {currentTF}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden">
                  {centerTab === "chart" ? (
                    <div className="flex flex-col h-full">
                      <div className="flex-1 relative min-h-0">
                        <Chart
                          instrument={activeInstrument}
                          initialData={marketData[activeInstrument?.id]}
                        />
                      </div>
                      {!chartExpanded && <BottomPanel />}
                    </div>
                  ) : (
                    <MarketTable
                      category={marketView === "favorites" ? "fav" : "all"}
                      store={storeRef.current}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col"
          style={{
            height: "100dvh",
            paddingBottom: showMobileOrderPopup ? "0px" : "60px",
            background: "var(--color-bg)",
          }}
        >
          <Topbar />
          <div className="flex-1 overflow-hidden relative">
            {mobileTab === "table" && (
              <div className="h-full flex flex-col overflow-hidden">
                <div
                  className="flex-shrink-0 flex items-center px-2"
                  style={{
                    background: "var(--color-bgSurface)",
                    borderBottom: "1px solid var(--color-borderStrong)",
                  }}
                >
                  <button
                    onClick={() => setMarketView("assets")}
                    className="flex-1 py-3 text-[11px] font-syne font-bold border-b-2 transition-all"
                    style={{
                      color:
                        marketView === "assets"
                          ? "var(--color-text)"
                          : "var(--color-textSecondary)",
                      borderColor:
                        marketView === "assets"
                          ? "var(--color-green)"
                          : "transparent",
                    }}
                  >
                    <i className="fas fa-boxes-stacked text-[10px] mr-1" />
                    Assets
                  </button>
                  <button
                    onClick={() => setMarketView("favorites")}
                    className="flex-1 py-3 text-[11px] font-syne font-bold border-b-2 transition-all"
                    style={{
                      color:
                        marketView === "favorites"
                          ? "var(--color-text)"
                          : "var(--color-textSecondary)",
                      borderColor:
                        marketView === "favorites"
                          ? "var(--color-orange)"
                          : "transparent",
                    }}
                  >
                    <i className="fas fa-star text-[10px] mr-1" />
                    Favorites {favorites.length > 0 && `(${favorites.length})`}
                  </button>
                </div>
                <MobileMarketCategoryBar
                  activeCategory={mobileMarketCategory}
                  onSelectCategory={setMobileMarketCategory}
                />
                <div className="flex-1 overflow-hidden">
                  <MarketTable
                    category={
                      marketView === "favorites" ? "fav" : mobileMarketCategory
                    }
                    store={storeRef.current}
                  />
                </div>
              </div>
            )}
            {mobileTab === "chart" && (
              <div className="h-full flex flex-col">
                {activeInstrument ? (
                  <div className="flex-1 relative min-h-0">
                    <Chart
                      instrument={activeInstrument}
                      initialData={marketData[activeInstrument?.id]}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl"
                      style={{
                        background: "var(--color-orangeBg)",
                        border: "1px solid var(--color-orangeBorder)",
                        color: "var(--color-text)",
                      }}
                    >
                      <i className="fas fa-chart-line" />
                    </div>
                    <p
                      className="font-syne font-extrabold text-base mb-1"
                      style={{ color: "var(--color-text)" }}
                    >
                      No Instrument Selected
                    </p>
                    <p
                      className="text-[11px] font-mono"
                      style={{ color: "var(--color-textSecondary)" }}
                    >
                      Go to the Market tab and tap any symbol
                    </p>
                    <button
                      onClick={() => setMobileTab("table")}
                      className="px-5 py-2.5 rounded-xl font-syne font-bold text-sm"
                      style={{
                        background: "var(--color-orangeBg)",
                        border: "1px solid var(--color-orangeBorder)",
                        color: "var(--color-text)",
                      }}
                    >
                      <i className="fas fa-table mr-2" />
                      Go to Market
                    </button>
                  </div>
                )}
              </div>
            )}
            {mobileTab === "orders" && (
              <div className="h-full overflow-hidden">
                <MobileOrdersPanel
                  portfolio={portfolio}
                  marketData={marketData}
                />
              </div>
            )}
            {mobileTab === "analytics" && (
              <div className="h-full overflow-y-auto custom-scrollbar">
                <Analytics isMobile={true} />
              </div>
            )}
            {mobileTab === "account" && (
              <div className="h-full overflow-hidden">
                <MobileAccountHub
                  balance={balance}
                  portfolio={portfolio}
                  marketData={marketData}
                />
              </div>
            )}
          </div>

          {showMobileOrderPopup && activeInstrument && (
            <div
              className="fixed inset-0 z-[300] flex items-end justify-center backdrop-blur-sm"
              style={{ background: "var(--color-bgOverlay)" }}
              onClick={() => setShowMobileOrderPopup(false)}
            >
              <div
                className="w-full rounded-t-2xl animate-slide-up"
                style={{
                  background: "var(--color-bgSurface)",
                  border: "1px solid var(--color-orangeBorder)",
                  maxHeight: "92vh",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-between px-4 py-3 rounded-t-2xl"
                  style={{
                    background: "var(--color-bgSurface)",
                    borderBottom: "1px solid var(--color-borderMedium)",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full animate-pulse"
                      style={{ background: "var(--color-green)" }}
                    />
                    <span
                      className="font-syne text-sm font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {activeInstrument.id}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowMobileOrderPopup(false)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{
                      background: "var(--color-bgCard)",
                      color: "var(--color-textSecondary)",
                    }}
                  >
                    <i className="fas fa-xmark" />
                  </button>
                </div>
                <div
                  className="overflow-y-auto custom-scrollbar pb-4"
                  style={{ flexShrink: 0 }}
                >
                  <OrderPanel
                    instrument={activeInstrument}
                    store={storeRef.current}
                    isMobile={true}
                  />
                </div>
              </div>
            </div>
          )}

          {!showMobileOrderPopup && (
            <div
              className="fixed bottom-0 left-0 right-0 z-[200]"
              style={{
                height: "60px",
                background: "var(--color-bgSurface)",
                borderTop: "1px solid var(--color-border)",
              }}
            >
              <div className="flex items-stretch h-full">
                {mobileTabs.map((tab) => {
                  const isActive = mobileTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setMobileTab(tab.id)}
                      className="flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-all active:scale-95"
                      style={{
                        color: isActive
                          ? "var(--color-text)"
                          : "var(--color-textDim)",
                      }}
                    >
                      {isActive && (
                        <div
                          className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full"
                          style={{ background: "var(--color-orange)" }}
                        />
                      )}
                      <i className={`fas ${tab.icon} text-[14px]`} />
                      <span className="text-[8px] font-syne font-bold">
                        {tab.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <BuyModal instrument={activeInstrument} store={storeRef.current} />
      <SellModal instrument={activeInstrument} store={storeRef.current} />
      <OrderModal store={storeRef.current} />
      <QuestionnaireModal
        isOpen={showQuestionnaire}
        onClose={() => setShowQuestionnaire(false)}
        onComplete={() => setShowQuestionnaire(false)}
      />
      <Toast />

      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        .animate-float { animation: float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay: -8s }
        @keyframes slide-up { from { transform: translateY(100%) } to { transform: translateY(0) } }
        .animate-slide-up { animation: slide-up 0.3s ease-out }
        .custom-scrollbar { scrollbar-width: thin; scrollbar-color: var(--color-scrollbarThumb) var(--color-scrollbarTrack); }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: var(--color-scrollbarTrack); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
        * { scrollbar-width: thin; scrollbar-color: var(--color-scrollbarThumb) var(--color-scrollbarTrack); }
        *::-webkit-scrollbar { width: 6px; height: 6px; }
        *::-webkit-scrollbar-track { background: var(--color-scrollbarTrack); }
        *::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default TradingTerminal;